/**
 * TagInput Component
 *
 * A tag management component that allows adding, removing, and autocompleting tags.
 * Supports keyboard navigation and accessible interactions.
 */

import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/utils/cn';
import { TagBadge } from '@/components/ui/badge';

// ============================================
// TYPES
// ============================================

export interface TagInputProps {
  /** Currently selected tags */
  tags: string[];
  /** Callback when tags change */
  onChange: (tags: string[]) => void;
  /** Available tags for autocomplete */
  availableTags?: string[] | undefined;
  /** Placeholder text */
  placeholder?: string | undefined;
  /** Whether the input is disabled */
  disabled?: boolean | undefined;
  /** Additional class names */
  className?: string | undefined;
  /** Maximum number of tags allowed */
  maxTags?: number | undefined;
  /** Label for accessibility */
  'aria-label'?: string | undefined;
}

// ============================================
// HELPERS
// ============================================

/**
 * Normalize a tag string for comparison
 */
function normalizeTag(tag: string): string {
  return tag.toLowerCase().trim();
}

/**
 * Check if a tag already exists in the list
 */
function tagExists(tags: string[], newTag: string): boolean {
  const normalized = normalizeTag(newTag);
  return tags.some((t) => normalizeTag(t) === normalized);
}

/**
 * Filter available tags based on input
 */
function filterSuggestions(
  availableTags: string[],
  currentTags: string[],
  input: string
): string[] {
  if (!input.trim()) return [];

  const normalizedInput = normalizeTag(input);

  return availableTags.filter((tag) => {
    // Don't suggest already selected tags
    if (tagExists(currentTags, tag)) return false;
    // Match partial input
    return normalizeTag(tag).includes(normalizedInput);
  });
}

// ============================================
// MAIN COMPONENT
// ============================================

export function TagInput({
  tags,
  onChange,
  availableTags = [],
  placeholder = 'Add tags...',
  disabled = false,
  className,
  maxTags,
  'aria-label': ariaLabel = 'Tag input',
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update suggestions when input changes
  useEffect(() => {
    const filtered = filterSuggestions(availableTags, tags, inputValue);
    setSuggestions(filtered.slice(0, 5)); // Limit to 5 suggestions
    setSelectedSuggestionIndex(-1);
  }, [inputValue, availableTags, tags]);

  // Add a tag
  const addTag = useCallback(
    (tag: string) => {
      const trimmedTag = tag.trim();
      if (!trimmedTag) return;
      if (tagExists(tags, trimmedTag)) return;
      if (maxTags && tags.length >= maxTags) return;

      onChange([...tags, trimmedTag]);
      setInputValue('');
      setSuggestions([]);
    },
    [tags, onChange, maxTags]
  );

  // Remove a tag
  const removeTag = useCallback(
    (tagToRemove: string) => {
      onChange(tags.filter((tag) => tag !== tagToRemove));
    },
    [tags, onChange]
  );

  // Handle input change
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Check for comma - add tag if found
    if (value.includes(',')) {
      const parts = value.split(',');
      const tagToAdd = parts[0];
      if (tagToAdd) {
        addTag(tagToAdd);
      }
      // Keep any text after the comma
      setInputValue(parts.slice(1).join(','));
    } else {
      setInputValue(value);
    }
  };

  // Handle keyboard events
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && suggestions[selectedSuggestionIndex]) {
          addTag(suggestions[selectedSuggestionIndex]);
        } else if (inputValue.trim()) {
          addTag(inputValue);
        }
        break;

      case 'Backspace':
        if (!inputValue && tags.length > 0) {
          // Remove last tag when backspace pressed with empty input
          removeTag(tags[tags.length - 1]!);
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (suggestions.length > 0) {
          setSelectedSuggestionIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (suggestions.length > 0) {
          setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
        }
        break;

      case 'Escape':
        setSuggestions([]);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    addTag(suggestion);
    inputRef.current?.focus();
  };

  // Handle focus/blur
  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Don't blur if clicking on a suggestion
    if (containerRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsFocused(false);
    setSuggestions([]);
  };

  // Check if max tags reached
  const isMaxReached = maxTags !== undefined && tags.length >= maxTags;

  return (
    <div ref={containerRef} className={cn('relative', className)} aria-label={ariaLabel}>
      {/* Tag container */}
      <div
        className={cn(
          'flex flex-wrap gap-2 rounded-md border bg-white p-2 min-h-[42px]',
          'transition-colors',
          isFocused
            ? 'border-brand-500 ring-2 ring-brand-500'
            : 'border-gray-300 hover:border-gray-400',
          disabled && 'opacity-50 cursor-not-allowed bg-gray-50'
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Existing tags */}
        {tags.map((tag) => (
          <TagBadge key={tag} tag={tag} removable={!disabled} onRemove={removeTag} size="sm" />
        ))}

        {/* Input field */}
        {!isMaxReached && !disabled && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={tags.length === 0 ? placeholder : ''}
            disabled={disabled}
            className={cn(
              'flex-1 min-w-[120px] bg-transparent text-sm outline-none',
              'placeholder:text-gray-400'
            )}
            aria-autocomplete="list"
            aria-controls="tag-suggestions"
            aria-expanded={suggestions.length > 0}
            aria-activedescendant={
              selectedSuggestionIndex >= 0 ? `tag-suggestion-${selectedSuggestionIndex}` : undefined
            }
          />
        )}

        {/* Max reached indicator */}
        {isMaxReached && (
          <span className="text-xs text-gray-400 self-center">Max {maxTags} tags</span>
        )}
      </div>

      {/* Suggestions dropdown */}
      {suggestions.length > 0 && isFocused && (
        <ul
          id="tag-suggestions"
          role="listbox"
          className={cn(
            'absolute z-10 mt-1 w-full rounded-md border border-gray-200',
            'bg-white shadow-lg max-h-48 overflow-auto'
          )}
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion}
              id={`tag-suggestion-${index}`}
              role="option"
              aria-selected={index === selectedSuggestionIndex}
              className={cn(
                'flex items-center gap-2 px-3 py-2 cursor-pointer text-sm',
                'transition-colors',
                index === selectedSuggestionIndex
                  ? 'bg-brand-50 text-brand-700'
                  : 'hover:bg-gray-50'
              )}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseDown={(e) => e.preventDefault()} // Prevent blur
            >
              <Plus className="h-3 w-3 text-gray-400" />
              {suggestion}
            </li>
          ))}
        </ul>
      )}

      {/* Helper text */}
      <p className="mt-1 text-xs text-gray-500">Press Enter or comma to add tags</p>
    </div>
  );
}

// ============================================
// SIMPLE TAG DISPLAY
// ============================================

interface TagListProps {
  tags: string[];
  onTagClick?: ((tag: string) => void) | undefined;
  onRemove?: ((tag: string) => void) | undefined;
  removable?: boolean | undefined;
  className?: string | undefined;
  maxVisible?: number | undefined;
}

export function TagList({
  tags,
  onTagClick,
  onRemove,
  removable = false,
  className,
  maxVisible,
}: TagListProps) {
  const visibleTags = maxVisible ? tags.slice(0, maxVisible) : tags;
  const hiddenCount = maxVisible ? tags.length - maxVisible : 0;

  if (tags.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {visibleTags.map((tag) => (
        <TagBadge
          key={tag}
          tag={tag}
          onClick={onTagClick}
          onRemove={removable ? onRemove : undefined}
          removable={removable}
          size="sm"
        />
      ))}
      {hiddenCount > 0 && (
        <span className="inline-flex items-center text-xs text-gray-500 px-2">
          +{hiddenCount} more
        </span>
      )}
    </div>
  );
}
